import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  LinearProgress,
  Button
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Campaign as CampaignIcon,
  VideoLibrary as VideoLibraryIcon,
  AttachMoney as MoneyIcon,
  Visibility as VisibilityIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';

export default function AdminOverview() {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalCampaigns: 0,
    totalSubmissions: 0,
    totalRevenue: 0,
    platformFees: 0,
    activeUsers: 0,
    pendingReviews: 0,
    systemHealth: 'good'
  });

  const [recentActivity, setRecentActivity] = useState([]);

  useEffect(() => {
    // Mock data - replace with actual Firebase queries
    setStats({
      totalUsers: 2847,
      totalCampaigns: 156,
      totalSubmissions: 1893,
      totalRevenue: 45780.50,
      platformFees: 4578.05,
      activeUsers: 1205,
      pendingReviews: 23,
      systemHealth: 'good'
    });

    setRecentActivity([
      {
        id: 1,
        type: 'user_registration',
        message: 'New user registered: john_creator',
        timestamp: new Date(Date.now() - 10 * 60 * 1000),
        severity: 'info'
      },
      {
        id: 2,
        type: 'campaign_created',
        message: 'New campaign created: "Tech Reviews 2024"',
        timestamp: new Date(Date.now() - 25 * 60 * 1000),
        severity: 'success'
      },
      {
        id: 3,
        type: 'payout_processed',
        message: 'Payout processed: $250.00 to user clip_master',
        timestamp: new Date(Date.now() - 45 * 60 * 1000),
        severity: 'success'
      },
      {
        id: 4,
        type: 'review_needed',
        message: 'Submission flagged for manual review',
        timestamp: new Date(Date.now() - 60 * 60 * 1000),
        severity: 'warning'
      }
    ]);
  }, []);

  const StatCard = ({ title, value, icon, color = 'primary', prefix = '', suffix = '', trend = null }) => (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography color="text.secondary" gutterBottom variant="overline">
              {title}
            </Typography>
            <Typography variant="h4" sx={{ color: `${color}.main`, fontWeight: 'bold' }}>
              {prefix}{typeof value === 'number' ? value.toLocaleString() : value}{suffix}
            </Typography>
            {trend && (
              <Typography variant="body2" color={trend > 0 ? 'success.main' : 'error.main'}>
                {trend > 0 ? '+' : ''}{trend}% vs last month
              </Typography>
            )}
          </Box>
          <Box sx={{ color: `${color}.main`, opacity: 0.7 }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const getActivityIcon = (type) => {
    switch (type) {
      case 'user_registration':
        return <PeopleIcon color="primary" />;
      case 'campaign_created':
        return <CampaignIcon color="success" />;
      case 'payout_processed':
        return <MoneyIcon color="success" />;
      case 'review_needed':
        return <WarningIcon color="warning" />;
      default:
        return <CheckCircleIcon />;
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'success':
        return 'success';
      case 'warning':
        return 'warning';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
        Platform Overview
      </Typography>

      {/* Key Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Users"
            value={stats.totalUsers}
            icon={<PeopleIcon fontSize="large" />}
            color="primary"
            trend={15.2}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active Campaigns"
            value={stats.totalCampaigns}
            icon={<CampaignIcon fontSize="large" />}
            color="info"
            trend={8.7}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Submissions"
            value={stats.totalSubmissions}
            icon={<VideoLibraryIcon fontSize="large" />}
            color="secondary"
            trend={22.4}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Platform Revenue"
            value={stats.platformFees}
            icon={<MoneyIcon fontSize="large" />}
            color="success"
            prefix="$"
            trend={12.8}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* System Health */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              System Health
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Overall Status
              </Typography>
              <Chip 
                label={stats.systemHealth === 'good' ? 'Healthy' : 'Issues Detected'}
                color={stats.systemHealth === 'good' ? 'success' : 'error'}
                icon={stats.systemHealth === 'good' ? <CheckCircleIcon /> : <WarningIcon />}
              />
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Active Users (24h)
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                {stats.activeUsers}
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={(stats.activeUsers / stats.totalUsers) * 100}
                sx={{ mt: 1 }}
              />
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Pending Reviews
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                {stats.pendingReviews}
              </Typography>
              {stats.pendingReviews > 20 && (
                <Button size="small" color="warning" sx={{ mt: 1 }}>
                  Review Now
                </Button>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Recent Activity */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Recent Activity
            </Typography>
            <List>
              {recentActivity.map((activity, index) => (
                <ListItem key={activity.id} sx={{ px: 0 }}>
                  <ListItemIcon>
                    {getActivityIcon(activity.type)}
                  </ListItemIcon>
                  <ListItemText
                    primary={activity.message}
                    secondary={activity.timestamp.toLocaleString()}
                  />
                  <Chip 
                    label={activity.severity} 
                    color={getSeverityColor(activity.severity)}
                    size="small"
                  />
                </ListItem>
              ))}
            </List>
            <Button variant="outlined" fullWidth sx={{ mt: 2 }}>
              View All Activity
            </Button>
          </Paper>
        </Grid>
      </Grid>

      {/* Quick Actions */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
          Quick Actions
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Button variant="contained" fullWidth sx={{ bgcolor: '#ff5252', '&:hover': { bgcolor: '#ff3939' } }}>
              Send Notification
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button variant="outlined" fullWidth>
              Export Data
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button variant="outlined" fullWidth>
              System Backup
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button variant="outlined" fullWidth color="warning">
              Maintenance Mode
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
}
